/**
 * Structured integration guide data for all LMS types.
 * Used by both the frontend help page and backend PDF generation.
 *
 * Format: each step has a heading and markdown-like body content.
 * Lists are separated by \n. Inline code with backticks.
 */

export type LmsType = 'moodle' | 'tutorlms' | 'learndash' | 'xtramile';

export interface GuideStep {
  title: string;
  body: string;
  note?: string;
}

export interface GuideError {
  error: string;
  cause: string;
  solution: string;
}

export interface IntegrationGuide {
  lmsType: LmsType;
  displayName: string;
  tagline: string;
  description: string;
  estimatedTime: string;
  prerequisites: string[];
  steps: GuideStep[];
  requiredFunctions?: { name: string; description: string; required: boolean }[];
  commonErrors: GuideError[];
  securityTips: string[];
  officialDocsUrl: string;
  officialDocsLabel: string;
}

const OUR_IP = '5.249.254.24';

export const INTEGRATION_GUIDES: Record<LmsType, IntegrationGuide> = {
  moodle: {
    lmsType: 'moodle',
    displayName: 'Moodle',
    tagline: 'Open source LMS',
    description:
      'Moodle bruker Web Services med REST-protokoll. Vi henter kurs, påmeldinger, fullføringsstatus og karakterdata.',
    estimatedTime: '10–15 minutter',
    prerequisites: [
      'Moodle 3.5 eller nyere (anbefalt 4.x)',
      'Du må være Moodle-administrator',
      'Moodle må være tilgjengelig fra internett via HTTPS',
      `Hvis Moodle har IP-hvitliste: legg til ${OUR_IP}`
    ],
    steps: [
      {
        title: 'Slå på Web Services globalt',
        body:
          '1. Gå til **Site administration → Advanced features**\n' +
          '2. Sett "Enable web services" til **Yes**\n' +
          '3. Klikk **Save changes**'
      },
      {
        title: 'Aktiver REST-protokollen',
        body:
          '1. **Site administration → Plugins → Web services → Manage protocols**\n' +
          '2. Klikk øye-ikonet ved "REST protocol" slik at den blir aktivert',
        note: 'Ikke aktiver SOAP eller XML-RPC (deprecated).'
      },
      {
        title: 'Opprett en dedikert bruker for integrasjonen',
        body:
          '1. **Site administration → Users → Accounts → Add a new user**\n' +
          '2. Username: `kompetanseattest_api`\n' +
          '3. Sett et sterkt passord (brukeren logger aldri inn direkte)\n' +
          '4. Fyll inn fornavn, etternavn og en gyldig e-post',
        note:
          'Bruk IKKE din personlige admin-konto. Dedikert bruker gjør det enkelt å trekke tilbake tilgang senere.'
      },
      {
        title: 'Lag en rolle med nødvendige rettigheter',
        body:
          '1. **Site administration → Users → Permissions → Define roles → Add a new role**\n' +
          '2. Name: `Kompetanseattest Web Service`\n' +
          '3. Short name: `ws_kompetanseattest`\n' +
          '4. Kryss av for Context types: **System**\n' +
          '5. Aktiver disse rettighetene:\n' +
          '   - `webservice/rest:use`\n' +
          '   - `moodle/site:viewfullnames`\n' +
          '   - `moodle/user:viewhiddendetails`\n' +
          '   - `moodle/course:view`\n' +
          '   - `moodle/course:viewhiddencourses`\n' +
          '   - `moodle/grade:viewall` (for karakter-henting)\n' +
          '6. Klikk **Create this role**\n' +
          '7. **Assign system roles** → velg rollen → legg til brukeren'
      },
      {
        title: 'Opprett den eksterne tjenesten',
        body:
          '1. **Site administration → Plugins → Web services → External services → Add**\n' +
          '2. Name: `Kompetanseattest`\n' +
          '3. Short name: `kompetanseattest`\n' +
          '4. Enabled: ✓ Yes\n' +
          '5. Authorised users only: ✓ **Yes** (viktig for sikkerhet)\n' +
          '6. Klikk **Add service**'
      },
      {
        title: 'Legg til funksjoner i tjenesten',
        body:
          'Klikk **Functions**-lenken ved tjenesten, så **Add functions**. Se listen under.'
      },
      {
        title: 'Autoriser brukeren for tjenesten',
        body:
          '1. Klikk **Authorised users** på tjenesten\n' +
          '2. Velg `kompetanseattest_api` fra høyre kolonne og klikk **Add**'
      },
      {
        title: 'Opprett token',
        body:
          '1. **Site administration → Plugins → Web services → Manage tokens → Create token**\n' +
          '2. User: `kompetanseattest_api`\n' +
          '3. Service: `Kompetanseattest`\n' +
          `4. IP restriction: \`${OUR_IP}\` (anbefalt)\n` +
          '5. Valid until: la stå tom eller sett 1 år frem i tid\n' +
          '6. Klikk **Save changes**\n' +
          '7. **Kopier token-verdien umiddelbart** – den vises kun én gang'
      },
      {
        title: 'Lim inn i Kompetanseattest',
        body:
          '1. **Moodle URL:** `https://din-moodle.no` (uten skråstrek, uten /login e.l.)\n' +
          '2. **API-token:** verdien fra forrige steg\n' +
          '3. Klikk **Test tilkobling**'
      }
    ],
    requiredFunctions: [
      {
        name: 'core_course_get_courses',
        description: 'Liste over kurs (for kurs-dropdown)',
        required: true
      },
      {
        name: 'core_enrol_get_enrolled_users',
        description: 'Deltakere per kurs',
        required: true
      },
      {
        name: 'core_completion_get_course_completion_status',
        description: 'Fullføringsstatus',
        required: true
      },
      {
        name: 'core_user_get_users_by_field',
        description: 'Brukerinfo (e-post, mobil) for identitetsmatching',
        required: true
      },
      {
        name: 'gradereport_user_get_grade_items',
        description: 'Karakter/poeng på attesten',
        required: false
      }
    ],
    commonErrors: [
      {
        error: 'Invalid token',
        cause: 'Token feil eller utløpt',
        solution: 'Generer ny token (steg 8)'
      },
      {
        error: 'Access denied',
        cause: 'Brukeren mangler capability webservice/rest:use',
        solution: 'Se steg 4 – legg til rettigheten i rollen'
      },
      {
        error: 'Function not found',
        cause: 'Funksjon mangler i tjenesten',
        solution: 'Se steg 6 og legg til funksjonen'
      },
      {
        error: 'Invalid response / HTML tilbake',
        cause: 'Moodle-URL peker på /login eller tilsvarende',
        solution: 'Bruk kun base-URL uten sti'
      },
      {
        error: 'IP not allowed',
        cause: 'Token har IP-restriksjon som ikke matcher',
        solution: `Sett IP til ${OUR_IP}`
      }
    ],
    securityTips: [
      `Bruk alltid IP-restriksjon på token (\`${OUR_IP}\`)`,
      'Bruk dedikert brukerkonto, ikke din personlige admin-konto',
      'Rollen i steg 4 gir kun det som trengs – ikke bredere',
      'Sett utløpsdato på token og fornye periodisk (f.eks. årlig)',
      'Moodle logger alle API-kall under Site administration → Reports → Logs → Web services',
      'Deaktiver brukeren for umiddelbar tilbakekalling av all tilgang'
    ],
    officialDocsUrl: 'https://docs.moodle.org/en/Using_web_services',
    officialDocsLabel: 'Moodle Web Services-dokumentasjon'
  },

  tutorlms: {
    lmsType: 'tutorlms',
    displayName: 'TutorLMS',
    tagline: 'WordPress-basert LMS (krever TutorLMS Pro)',
    description:
      'TutorLMS har sitt eget Rest API-system med Client ID/Secret (IKKE WordPress Application Password). For karakter-henting kreves TutorLMS Pro.',
    estimatedTime: '5 minutter',
    prerequisites: [
      'TutorLMS Pro installert og aktivert (påkrevd for kursfullføringsdata via API)',
      'WordPress-administrator-tilgang',
      'WordPress-nettstedet må være tilgjengelig over HTTPS'
    ],
    steps: [
      {
        title: 'Logg inn som WordPress-administrator',
        body: 'Gå til WordPress Admin-panelet på din TutorLMS-installasjon.'
      },
      {
        title: 'Gå til TutorLMS Rest API-panelet',
        body:
          '1. I venstre meny: **TutorLMS → Tools → Rest API**\n' +
          '2. I noen versjoner ligger dette under **TutorLMS → Rest API** direkte'
      },
      {
        title: 'Opprett et nytt API-nøkkelpar',
        body:
          '1. Klikk **"Legg til ny"** (Add New)\n' +
          '2. Gi nøkkelen et navn, f.eks. `Kompetanseattest`\n' +
          '3. Sett **Tillatelse** til **"All"** (eller minst **Read/Write**)\n' +
          '4. Klikk **Opprett**'
      },
      {
        title: 'Kopier Client ID og Client Secret',
        body:
          'Etter opprettelse vises to verdier:\n' +
          '- **Client ID** (API Key): starter med `key_...`\n' +
          '- **Client Secret** (Hemlig): starter med `secret_...`\n\n' +
          '**Kopier begge umiddelbart** – Secret vises kun én gang og kan ikke hentes igjen senere.',
        note:
          'VIKTIG: Dette er IKKE WordPress Application Password. TutorLMS har sitt eget API-system som er helt atskilt.'
      },
      {
        title: 'Lim inn i Kompetanseattest',
        body:
          '1. **WordPress URL:** `https://din-side.no` (base-URL uten /wp-admin)\n' +
          '2. **Client ID:** hele `key_...`-verdien\n' +
          '3. **Client Secret:** hele `secret_...`-verdien\n' +
          '4. Klikk **Test tilkobling**'
      }
    ],
    commonErrors: [
      {
        error: '401 Unauthorized / rest_forbidden',
        cause: 'Feil Client ID eller Secret, eller bruk av WordPress Application Password i stedet for TutorLMS-nøkler',
        solution: 'Verifiser at verdier starter med key_ og secret_ (ikke vanlig passord). Opprett nytt par om nødvendig.'
      },
      {
        error: '403 Forbidden / Access denied',
        cause: 'TutorLMS Rest API er ikke aktivert, eller manglende TutorLMS Pro',
        solution: 'Bekreft at TutorLMS Pro er installert og Rest API er aktivert.'
      },
      {
        error: '400 Invalid input på /enrollments',
        cause: 'Forventet oppførsel – bekrefter at Pro er installert',
        solution: 'Ingen handling kreves – håndteres automatisk av vår klient.'
      },
      {
        error: 'HTML tilbake i stedet for JSON',
        cause: 'WordPress URL er feil eller peker på et underdomene uten REST API',
        solution: 'Sjekk at base-URL er riktig og at /wp-json returnerer JSON.'
      }
    ],
    securityTips: [
      'Dedikerte API-nøkler for hver tredjepartsintegrasjon (ikke del på tvers)',
      'Bruk "All"- eller "Read/Write"-tillatelse, ikke bredere',
      'Hvis dere senere vil revokere tilgang: slett nøkkelparet i TutorLMS Rest API-menyen',
      'Secret-verdien kan ikke hentes tilbake – må opprettes på nytt hvis den er mistet'
    ],
    officialDocsUrl: 'https://docs.themeum.com/tutor-lms/developer-documentation/rest-apis-for-tutor-lms-pro/',
    officialDocsLabel: 'TutorLMS Pro REST API-dokumentasjon'
  },

  learndash: {
    lmsType: 'learndash',
    displayName: 'LearnDash',
    tagline: 'WordPress-basert LMS (REST API i alle lisenser)',
    description:
      'LearnDash bruker WordPress sin standard REST API v2. Autentisering skjer via WordPress Application Passwords.',
    estimatedTime: '5 minutter',
    prerequisites: [
      'LearnDash (REST API v2 er tilgjengelig i alle lisensplaner)',
      'WordPress 5.6 eller nyere (for Application Passwords)',
      'WordPress-administrator-tilgang',
      'WordPress-nettstedet må være tilgjengelig over HTTPS'
    ],
    steps: [
      {
        title: 'Logg inn som WordPress-administrator',
        body: 'Gå til WordPress Admin-panelet på din LearnDash-installasjon.'
      },
      {
        title: 'Opprett eller identifiser en dedikert bruker',
        body:
          'Det anbefales å opprette en dedikert WordPress-bruker for integrasjonen, i stedet for å bruke personlig admin-konto:\n\n' +
          '1. **Brukere → Legg til ny**\n' +
          '2. Brukernavn: `kompetanseattest_api`\n' +
          '3. E-post: en teknisk e-postadresse\n' +
          '4. Rolle: **Administrator** (LearnDash REST API krever administrator-tilgang)\n' +
          '5. Klikk **Legg til ny bruker**'
      },
      {
        title: 'Åpne Application Passwords for brukeren',
        body:
          '1. **Brukere → Alle brukere** → klikk brukeren\n' +
          '2. Scroll ned til seksjonen **"Application Passwords"**\n' +
          '3. Hvis seksjonen mangler: WordPress-versjonen må være 5.6+ og Application Passwords må ikke være deaktivert via filter'
      },
      {
        title: 'Generer et Application Password',
        body:
          '1. Under **"New Application Password Name"**, skriv `Kompetanseattest`\n' +
          '2. Klikk **"Add New Application Password"**\n' +
          '3. WordPress viser passordet i format `xxxx xxxx xxxx xxxx xxxx xxxx` (24 tegn i 6 grupper)\n' +
          '4. **Kopier hele passordet umiddelbart** – det vises kun én gang'
      },
      {
        title: 'Lim inn i Kompetanseattest',
        body:
          '1. **WordPress URL:** `https://din-side.no` (base-URL uten /wp-admin)\n' +
          '2. **Brukernavn:** `kompetanseattest_api` (WordPress-brukernavnet)\n' +
          '3. **Application Password:** det genererte passordet (mellomrom er OK)\n' +
          '4. Klikk **Test tilkobling**'
      }
    ],
    commonErrors: [
      {
        error: '401 Unauthorized',
        cause: 'Feil brukernavn eller Application Password',
        solution: 'Verifiser brukernavn. Generer nytt Application Password om nødvendig.'
      },
      {
        error: '403 Forbidden',
        cause: 'Brukeren har ikke administrator-rolle',
        solution: 'Gi brukeren Administrator-rolle. LearnDash REST API krever dette.'
      },
      {
        error: 'Application Passwords-seksjonen mangler',
        cause: 'WordPress under 5.6 eller deaktivert via filter/plugin',
        solution: 'Oppgrader WordPress. Sjekk at ingen sikkerhetsplugin blokkerer Application Passwords.'
      },
      {
        error: 'rest_no_route / 404 på endpoints',
        cause: 'LearnDash REST API ikke aktivert i innstillinger',
        solution: 'Gå til **LearnDash LMS → Innstillinger → REST API** og aktiver.'
      }
    ],
    securityTips: [
      'Bruk dedikert WordPress-bruker (ikke personlig admin)',
      'Application Passwords kan revokeres fra brukerprofilen uten å påvirke passordet til brukeren',
      'Navngiving på password ("Kompetanseattest") gir audit-trail – se når det ble brukt',
      'Hvis dere mistenker lekkasje: revoker og generer nytt'
    ],
    officialDocsUrl: 'https://developers.learndash.com/rest-api/v2/',
    officialDocsLabel: 'LearnDash REST API v2-dokumentasjon'
  },

  xtramile: {
    lmsType: 'xtramile',
    displayName: 'Xtramile',
    tagline: 'Norsk e-læringsplattform',
    description:
      'Xtramile bruker et proprietært API med tenant-spesifikk URL og API-nøkkel. Kontakt Xtramile support for å få tilgang hvis din organisasjon ikke allerede har API aktivert.',
    estimatedTime: '5–10 minutter',
    prerequisites: [
      'Aktiv Xtramile-konto for din organisasjon',
      'API-tilgang må være aktivert (kontakt Xtramile support om nødvendig)',
      'Administrator-tilgang i Xtramile-portalen'
    ],
    steps: [
      {
        title: 'Logg inn i Xtramile-administrasjonspanelet',
        body: 'Gå til administrasjonspanelet for din Xtramile-instans.'
      },
      {
        title: 'Finn API-innstillinger',
        body:
          '1. Naviger til **Innstillinger → API** (nøyaktig plassering kan variere)\n' +
          '2. Hvis ingen API-seksjon vises: kontakt Xtramile support og be dem aktivere API-tilgang for din organisasjon'
      },
      {
        title: 'Generer eller hent API-nøkkel',
        body:
          '1. Klikk **"Generer ny API-nøkkel"** (eller tilsvarende)\n' +
          '2. Navngi nøkkelen: `Kompetanseattest`\n' +
          '3. **Kopier API-nøkkelen** umiddelbart – vises ofte kun én gang'
      },
      {
        title: 'Noter tenant-URL',
        body:
          'Xtramile har tenant-spesifikk URL, typisk i formatet:\n' +
          '- `https://din-organisasjon.xtramile.no`\n' +
          '- eller `https://xtramile.no/din-organisasjon`\n\n' +
          'Bekreft korrekt URL med Xtramile hvis dere er usikre.'
      },
      {
        title: 'Lim inn i Kompetanseattest',
        body:
          '1. **Tenant URL / Base URL:** som noteret over\n' +
          '2. **API-nøkkel:** verdien du kopierte\n' +
          '3. Klikk **Test tilkobling**'
      }
    ],
    commonErrors: [
      {
        error: '401 Unauthorized / Invalid API key',
        cause: 'Feil eller utløpt API-nøkkel',
        solution: 'Generer ny nøkkel og lim inn på nytt.'
      },
      {
        error: '403 Forbidden',
        cause: 'API-nøkkelen har ikke tilstrekkelige tillatelser',
        solution: 'Kontakt Xtramile support for å utvide tillatelsene.'
      },
      {
        error: '404 Not Found',
        cause: 'Feil tenant-URL',
        solution: 'Verifiser korrekt URL med Xtramile.'
      },
      {
        error: 'Tilkobling timeout',
        cause: 'Xtramile-instans ikke tilgjengelig fra internett',
        solution: 'Kontakt Xtramile om tilkoblingsproblemer.'
      }
    ],
    securityTips: [
      'Bruk dedikert API-nøkkel per integrasjon',
      'Revoker nøkler dere ikke lenger bruker',
      'Ikke del API-nøkkelen på e-post eller chat – bruk sikker kanal'
    ],
    officialDocsUrl: 'https://xtramile.no',
    officialDocsLabel: 'Xtramile'
  }
};
